// Extraído de ShineTab.tsx (Tarea 17 original) para que el export de Ganadores (AdminWinners.tsx)
// reutilice la MISMA resolución de catálogo y el MISMO armado de celdas 'X'/'' que ya usa la hoja
// "Todos los reconocimientos" del reporte de Brillamos Juntos, en vez de duplicarla.
import type { FundamentalCatalogItem } from '@/api/types';
import type { NominationRawExportRowDto } from '@/api/recognition';
import type { ExcelCellValue, FundamentalConHabitos } from '@/utils/exportExcelMultiSheet';

/** Un Fundamental activo con sus Hábitos activos, nombres YA traducidos al idioma pedido. */
export interface FundamentalConHabitosResueltos extends FundamentalConHabitos {
  habitos: { codigo: string; nombre: string }[];
}

/**
 * Filtra el catálogo a Fundamentales/Hábitos ACTIVOS y resuelve sus nombres al idioma pedido
 * (fallback a inglés y, si tampoco hay, al código). El orden es el DEL CATÁLOGO — instrucción de
 * negocio explícita (ver `ShineTab.tsx`, comentario de `buildTodosLosReconocimientosSheet`): no se
 * reordena. Un Fundamental que se queda sin Hábitos activos tras el filtro se cae de la lista.
 */
export function resolverFundamentalesConHabitos(
  catalogo: FundamentalCatalogItem[] | undefined, lang: string,
): FundamentalConHabitosResueltos[] {
  return (catalogo ?? [])
    .filter((fundamental) => fundamental.isActive)
    .map((fundamental) => ({
      nombre: fundamental.names?.[lang] ?? fundamental.names?.en ?? fundamental.code,
      habitos: (fundamental.dailyHabits ?? [])
        .filter((habito) => habito.isActive)
        .map((habito) => (
          { codigo: habito.code, nombre: habito.names?.[lang] ?? habito.names?.en ?? habito.code }
        )),
    }))
    .filter((fundamental) => fundamental.habitos.length > 0);
}

/** Una celda 'X'/'' por cada Hábito de la matriz, en el MISMO orden que sus columnas de encabezado. */
export function celdasDeHabitos(
  nominacion: NominationRawExportRowDto,
  fundamentalesConHabitos: FundamentalConHabitosResueltos[],
): ExcelCellValue[] {
  return fundamentalesConHabitos.flatMap((fundamental) => fundamental.habitos.map(
    (habito) => (nominacion.habitos[habito.codigo] ? 'X' : ''),
  ));
}

/**
 * Fix crítico (revisión de rama completa, 2026-08-31) — el export de Ganadores (`AdminWinners.tsx`
 * vía `construirHojaExcelNominaciones`) reusaba `resolverFundamentalesConHabitos` TAL CUAL, pero ese
 * export tiene un requisito DISTINTO al de "Todos los reconocimientos" (Brillamos Juntos): el propio
 * comentario de cabecera de `nominationsExportMapper.ts` documenta que la matriz de hábitos debe
 * cubrir la UNIÓN de todos los hábitos citados en las nominaciones exportadas, incluso si el catálogo
 * cambió entre una y otra. Con la función activo-solamente, un Hábito desactivado DESPUÉS de que
 * alguna nominación lo citara desaparecía del Excel sin dejar rastro — ni columna, ni celda, ni
 * aviso— en el documento que el comité usa para decidir premios.
 *
 * Esta función es la variante consciente de ese requisito: arranca de la misma resolución
 * activo-solamente (incluyendo Fundamentales que se quedarían sin hábitos activos, por si terminan
 * recibiendo uno retirado) y le agrega, código por código, cualquier Hábito citado en `nominaciones`
 * que NO esté en el catálogo activo. Dónde aterriza cada Hábito retirado:
 *   1. Si su Fundamental padre sigue ACTIVO en el catálogo, se agrega a las columnas de ESE
 *      Fundamental (después de sus hábitos activos) — sigue siendo, para quien lee el Excel, el mismo
 *      grupo temático de siempre.
 *   2. Si su Fundamental padre también está retirado, o el código ya no existe en ningún Fundamental
 *      del catálogo (borrado, no solo desactivado), se agrupa aparte bajo `etiquetaGrupoRetirados`
 *      (texto ya traducido, provisto por el caller) al final de la matriz — no hay un grupo activo
 *      razonable al que asignarlo.
 * `resolverFundamentalesConHabitos` (la función original) NO se modifica: sigue siendo la resolución
 * correcta para `ShineTab.tsx`, que es un reporte de plantilla fija sobre el catálogo VIGENTE.
 */
export function resolverFundamentalesConHabitosParaExport(
  catalogo: FundamentalCatalogItem[] | undefined,
  lang: string,
  nominaciones: readonly NominationRawExportRowDto[],
  etiquetaGrupoRetirados: string,
): FundamentalConHabitosResueltos[] {
  const catalogoCompleto = catalogo ?? [];

  const fundamentalesActivos = catalogoCompleto
    .filter((fundamental) => fundamental.isActive)
    .map((fundamental) => ({
      codigoFundamental: fundamental.code,
      nombre: fundamental.names?.[lang] ?? fundamental.names?.en ?? fundamental.code,
      habitos: (fundamental.dailyHabits ?? [])
        .filter((habito) => habito.isActive)
        .map((habito) => (
          { codigo: habito.code, nombre: habito.names?.[lang] ?? habito.names?.en ?? habito.code }
        )),
    }));

  const codigosActivos = new Set(
    fundamentalesActivos.flatMap((fundamental) => fundamental.habitos.map((habito) => habito.codigo)),
  );
  const codigosCitados = new Set<string>();
  nominaciones.forEach((fila) => {
    Object.keys(fila.habitos ?? {}).forEach((codigo) => codigosCitados.add(codigo));
  });
  const codigosRetirados = [...codigosCitados].filter((codigo) => !codigosActivos.has(codigo));

  const grupoRetirados: { codigo: string; nombre: string }[] = [];
  codigosRetirados.forEach((codigo) => {
    const fundamentalPadre = catalogoCompleto.find(
      (fundamental) => (fundamental.dailyHabits ?? []).some((habito) => habito.code === codigo),
    );
    const habitoOriginal = fundamentalPadre?.dailyHabits?.find((habito) => habito.code === codigo);
    const nombreHabito = habitoOriginal?.names?.[lang] ?? habitoOriginal?.names?.en ?? codigo;

    const grupoDelFundamentalPadre = fundamentalPadre?.isActive
      ? fundamentalesActivos.find((fundamental) => fundamental.codigoFundamental === fundamentalPadre.code)
      : undefined;

    if (grupoDelFundamentalPadre) {
      grupoDelFundamentalPadre.habitos.push({ codigo, nombre: nombreHabito });
    } else {
      grupoRetirados.push({ codigo, nombre: nombreHabito });
    }
  });

  const resultado: FundamentalConHabitosResueltos[] = fundamentalesActivos
    .filter((fundamental) => fundamental.habitos.length > 0)
    .map((fundamental) => ({ nombre: fundamental.nombre, habitos: fundamental.habitos }));

  if (grupoRetirados.length > 0) resultado.push({ nombre: etiquetaGrupoRetirados, habitos: grupoRetirados });

  return resultado;
}
